import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Volume2, Lock, Unlock, RefreshCw, Edit3, Bookmark, ExternalLink } from 'lucide-react';
import { vocabularyApi, lexiconApi } from '../../utils/api';
import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useWordEntry } from '../../shared/reader/useWordEntry';
import { WordEntryStatus } from '../../shared/reader/WordEntryStatus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getWordColor(strength) {
  if (strength >= 0.9) return '#D4AF37';
  if (strength >= 0.7) return 'hsl(var(--foreground))';
  if (strength >= 0.3) return '#EAB308';
  return '#EF4444';
}

export function WordDetailSheet({ open, onClose, word, languageCode, onStatusChange }) {
  const { wordData: data, setWordData: setData, wordLoading: loading, loadEntry, cancel } = useWordEntry({ languageCode });
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!open || !word || !languageCode) return;
    loadEntry(word, word.includes(' '));
    return cancel;
  }, [open, word, languageCode, loadEntry, cancel]);

  const playAudio = () => {
    if (playing) return;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlaying(true);
    const audio = new Audio(`${BACKEND_URL}/api/tts/speak?text=${encodeURIComponent(word)}&lang=${languageCode}`);
    audioRef.current = audio;
    audio.onended = () => setPlaying(false);
    audio.onerror = () => setPlaying(false);
    audio.play().catch(() => setPlaying(false));
  };

  const handleRefresh = async () => {
    try {
      toast.info('Enriching...');
      await vocabularyApi.enrichWord(word, languageCode, null, true);
      const res = await vocabularyApi.getWordData(word, languageCode);
      setData(res.data);
      toast.success('Enriched');
    } catch { toast.error('Enrichment failed'); }
  };

  const handleToggleLock = async (field) => {
    try {
      const res = await lexiconApi.toggleFieldLock(word, languageCode, field);
      setData(prev => ({ ...prev, locked_fields: res.data?.locked_fields || [] }));
      toast.success(`${field} ${res.data?.action}`);
    } catch { toast.error('Failed'); }
  };

  const strength = data?.user_state?.strength || 0;
  const status = data?.user_state?.status || 'new';
  const lockedFields = data?.locked_fields || [];
  const defs = data?.definitions || [];
  const ipa = data?.ipa;
  const translation = data?.user_state?.translation || data?.translation_en || '';
  const examples = data?.examples || [];
  const synonyms = data?.synonyms || [];
  const derivations = data?.derivations || [];
  const collocates = data?.collocates || [];
  const related = data?.related_expressions || [];
  const morphology = data?.morphology;
  const lemma = data?.lemma;
  const pos = data?.pos;
  const note = data?.user_state?.note || '';

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg p-0" data-testid="word-detail-sheet">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center gap-3">
            <SheetTitle className="text-2xl font-bold font-reader" style={{ color: getWordColor(strength) }}>
              {word}
            </SheetTitle>
            <button
              onClick={playAudio}
              disabled={playing}
              className={`p-1.5 rounded-full transition-all ${playing ? 'bg-primary/20 text-primary animate-pulse' : 'bg-muted hover:bg-muted/80 text-muted-foreground'}`}
              data-testid="detail-audio-btn"
            >
              <Volume2 className="h-4 w-4" />
            </button>
            <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto" onClick={handleRefresh} title="Refresh AI data">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge variant="outline" className="text-xs">{status}</Badge>
            {pos && <Badge variant="secondary" className="text-xs">{pos}</Badge>}
            {lemma && lemma !== word && (
              <span className="text-xs text-muted-foreground">lemma: <strong>{lemma}</strong></span>
            )}
            {ipa && <span className="text-xs font-mono text-muted-foreground">/{ipa}/</span>}
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)]">
          <WordEntryStatus data={data} testId="detail-word-entry-status" />
          {loading ? (
            <div className="flex items-center justify-center py-12" data-testid="word-detail-loading">
              <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="px-6 py-4 space-y-5" data-testid="word-detail-content">
              {/* Translation */}
              <Section title="Translation" locked={lockedFields.includes('translation_en')} onToggleLock={() => handleToggleLock('translation_en')}>
                {translation ? (
                  <p className="text-base">{translation}</p>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No translation yet</p>
                )}
              </Section>

              {/* Definitions */}
              <Section title="Definitions" locked={lockedFields.includes('definitions')} onToggleLock={() => handleToggleLock('definitions')} count={defs.length}>
                {defs.length > 0 ? (
                  <div className="space-y-2">
                    {defs.map((d, i) => (
                      <div key={i} className="flex gap-2 text-sm">
                        <span className="text-muted-foreground shrink-0">{i + 1}.</span>
                        <div>
                          {d.pos && <Badge variant="outline" className="text-[10px] mr-1.5 mb-0.5">{d.pos}</Badge>}
                          <span>{d.definition || d.text || d}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No definitions yet</p>
                )}
              </Section>

              {/* Morphology */}
              {morphology && (
                <Section title="Morphology">
                  <div className="text-sm space-y-1">
                    {Object.entries(morphology).map(([k, v]) => (
                      <div key={k} className="flex gap-2">
                        <span className="text-muted-foreground capitalize min-w-[80px]">{k}:</span>
                        <span>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Examples */}
              <Section title="Examples" count={examples.length}>
                {examples.length > 0 ? (
                  <div className="space-y-2">
                    {examples.map((ex, i) => (
                      <p key={i} className="text-sm italic text-muted-foreground border-l-2 border-primary/30 pl-3">"{ex}"</p>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No examples</p>
                )}
              </Section>

              {/* Synonyms */}
              <WordCloud title="Synonyms" items={synonyms} />

              {/* Derivations */}
              <WordCloud title="Derivations" items={derivations} />

              {/* Collocates */}
              <WordCloud title="Collocates" items={collocates} />

              {/* Related Expressions */}
              <WordCloud title="Related Expressions" items={related} />

              {/* Notes */}
              {note && (
                <Section title="Note">
                  <p className="text-sm whitespace-pre-wrap">{note}</p>
                </Section>
              )}

              {/* SRS Data */}
              <Section title="SRS Data">
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <div>Strength: <strong className="text-foreground">{(strength * 100).toFixed(0)}%</strong></div>
                  <div>Status: <strong className="text-foreground">{status}</strong></div>
                  {data?.user_state?.stability && (
                    <div>Stability: <strong className="text-foreground">{data.user_state.stability}</strong></div>
                  )}
                  {data?.user_state?.review_count != null && (
                    <div>Reviews: <strong className="text-foreground">{data.user_state.review_count}</strong></div>
                  )}
                  {data?.user_state?.last_seen && (
                    <div className="col-span-2">Last seen: <strong className="text-foreground">{new Date(data.user_state.last_seen).toLocaleDateString()}</strong></div>
                  )}
                </div>
              </Section>
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children, locked, onToggleLock, count }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}{count != null ? ` (${count})` : ''}</h3>
        {onToggleLock && (
          <button onClick={onToggleLock} className="p-0.5 rounded hover:bg-muted/50" title={locked ? 'Unlock' : 'Lock'}>
            {locked ? <Lock size={11} className="text-amber-500" /> : <Unlock size={11} className="text-muted-foreground/50" />}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function WordCloud({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <Section title={title} count={items.length}>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <Badge key={i} variant="outline" className="text-xs font-normal">{item}</Badge>
        ))}
      </div>
    </Section>
  );
}
